import { useRef, useState, type ChangeEvent } from "react";
import {
  ReceivableTaxDocument,
  PendingReceivableTaxDocument,
  ReceivableTaxDocumentType,
} from "../../../../utils/types";
import {
  DocumentDraft,
  createEmptyDocumentDraft,
  generateTempDocumentId,
  ACCEPTED_DOCUMENT_TYPES,
} from "../utils";
import { MAX_FILE_SIZE_BYTES, getFileSizeErrorMessage } from "@/shared/lib/fileUploadConstants";

export function useTaxDocuments() {
  const [documents, setDocuments] = useState<PendingReceivableTaxDocument[]>([]);
  const [draft, setDraft] = useState<DocumentDraft>(createEmptyDocumentDraft());
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.size > MAX_FILE_SIZE_BYTES) {
      setError(getFileSizeErrorMessage());
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }
    setDraft((prev) => ({ ...prev, file: file ?? undefined }));
    if (file) {
      setError(null);
    }
  };

  const handleFileButtonClick = (disabled?: boolean) => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const handleFileRemove = () => {
    setDraft((prev) => ({ ...prev, file: undefined }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setError(null);
  };

  const addDocument = () => {
    const documentType = draft.documentType;
    if (!documentType) {
      setError("Informe o tipo do documento");
      return;
    }
    const typedDocumentType = documentType as ReceivableTaxDocumentType;

    if (!draft.file) {
      setError("Selecione um arquivo para anexar");
      return;
    }

    const newDocument: PendingReceivableTaxDocument = {
      id: generateTempDocumentId(),
      documentType: typedDocumentType,
      description: draft.description.trim() || undefined,
      file: draft.file,
      createdAt: new Date().toISOString(),
    };

    setDocuments((prev) => [...prev, newDocument]);
    setDraft(createEmptyDocumentDraft());
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeDocument = (id: string) => {
    setDocuments((prev) => prev.filter((doc) => doc.id !== id));
  };

  const updateDraft = (updates: Partial<DocumentDraft>) => {
    setDraft((prev) => ({ ...prev, ...updates }));
  };

  const reset = () => {
    setDocuments([]);
    setDraft(createEmptyDocumentDraft());
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return {
    documents,
    draft,
    error,
    fileInputRef,
    acceptedTypes: ACCEPTED_DOCUMENT_TYPES,
    handleFileChange,
    handleFileButtonClick,
    handleFileRemove,
    addDocument,
    removeDocument,
    updateDraft,
    reset,
  };
}

